import useDocumentFilters from '@/components/Approval/Content/hooks/useDocumentFilters';
import useSWRInfinite from 'swr/infinite';
import { DocumentListParams, RecordListType } from '@/types/api';
import { apiURL } from '@/constant/apiURL';
import { getNestedDataFetcher } from '@/utils/apis';
import {
  useDeleteDocumentBookmarked,
  useSetDocumentBookmarked,
} from '@/hooks/apiHooks';
import { t } from 'i18next';
import { useSnackbar } from '@/hooks/global-tools';
import { MAX_LIST_SIZE } from '@/components/MobileApproval';
import usePlatform from '@/components/Record/hooks/usePlatform';
import { buildQueryParams } from '@/utils';

type P = {
  params?: DocumentListParams;
};

const getDocumentkey =
  (url: string, obj: DocumentListParams) =>
  <T extends Record<string, unknown>>(
    pageIndex: number,
    previousPageData: T[],
  ) => {
    if (previousPageData && !previousPageData.length) {
      return null;
    }
    const queryParam = buildQueryParams(obj as any);

    return `${url}?${queryParam}&currentPage=${pageIndex + 1}`;
  };

const useDocuments = ({ params }: P) => {
  const toast = useSnackbar();
  const { isMobilePath } = usePlatform();

  const { handleFilterClick, documentState, setDocumentState } =
    useDocumentFilters();

  const { internalParticipants, createdBy, documentTypes, ...restFilters } =
    documentState;

  const queryParams: any = {
    ...restFilters,
  };

  if (createdBy.length > 0) {
    queryParams.createdBy = createdBy.map((res) => res.passportId);
  }

  if (documentTypes.length > 0) {
    queryParams.documentTypes = documentTypes.join(',');
  }

  if (internalParticipants.length > 0) {
    queryParams.internalParticipants = internalParticipants
      .map((res) => res.passportId)
      .join(',');
  }

  const { data, size, setSize, isLoading, mutate, error } = useSWRInfinite<
    RecordListType[]
  >(
    getDocumentkey(
      apiURL.TALK_FLOW_DOCUMENT_LIST,
      params ? params : queryParams,
    ),
    getNestedDataFetcher<RecordListType[]>,
    {
      refreshInterval: isMobilePath ? 1000 : 0,
      shouldRetryOnError: false, // 에러 발생 시 재시도를 하지 않도록 설정
      revalidateOnFocus: false,
    },
  );

  const handleBookmark = async (docId: string, isBookmarked: boolean) => {
    try {
      // API 호출
      if (isBookmarked) {
        await useDeleteDocumentBookmarked(docId);
      } else {
        await useSetDocumentBookmarked(docId);
      }

      // useSWRInfinite의 데이터 구조는 RecordListType[][] (배열의 배열)
      // 각 페이지(배열)를 순회하면서 해당 docId를 찾아서 업데이트
      mutate((currentData: RecordListType[][] | undefined) => {
        if (!currentData) return currentData;

        return currentData.map((page: RecordListType[]) =>
          page.map((item) => {
            if (item.id === docId) {
              return { ...item, isBookmarked: !isBookmarked };
            }
            return item;
          }),
        );
      }, false);

      !isBookmarked &&
        toast.showSuccess({ message: t('즐겨찾기에 추가되었습니다.') });
    } catch (e) {
      console.error('북마크 처리 중 오류:', e);
      toast.showError({
        message: t('북마크 처리 중 오류가 발생했습니다.'),
      });
    }
  };

  const handleRefresh = () => mutate();

  const resultData =
    data && Array.isArray(data)
      ? data.reduce((prev, next) => prev.concat(next), [])
      : undefined;

  const isReachingEnd = data && data[data.length - 1]?.length < MAX_LIST_SIZE;

  const readMoreData = () => {
    setSize(size + 1);
  };

  const handleChange = () => {
    if (documentState.isRead === null) {
      setDocumentState({ ...documentState, isRead: false });
    } else {
      setDocumentState({ ...documentState, isRead: null });
    }
  };

  return {
    handleBookmark,
    handleRefresh,
    resultData,
    isReachingEnd,
    readMoreData,
    handleChange,
    isLoading,
    error,
    data,
  };
};
export default useDocuments;
